import {enableMapSet} from 'immer';
import React, {useEffect} from 'react';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import {PoppaysLoader} from '@lightricks/react-design-system';
import {ROUTES as CL_ROUTES} from '@contentlab/config/routesConstants';
import MainRoutes from '@/routes/main';
import OffPlatformReviewRoutes from '@/routes/off-platform-review/OffPlatformReviewRoutes';
import PublicAccessRoutes from '@/routes/public-access';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import pageViewEvents from '@/lib/marketing-events/pageViewEvents';
import {getEnv} from '@/config/environment';
import {ROUTES} from '@/config/routesConstants';
import ContentSubmissionAnalysisModal from '@/components/content-submission-analysis-modal';
import CreatorProfileModal from '@/components/creator-profile-modal';
import DeveloperOverrideModal from '@/components/developer-override-modal';
import EmberIframe from '@/components/ember-iframe';
import FlashMessagesService from '@/components/flash-messages-service';
import ModalContactSales from '@/components/modal-contact-sales';
import ModalLeverLimit from '@/components/modal-lever-limit';
import ModalSubscriptionWrapper from '@/components/modal-subscription-wrapper';
import ModalTermsOfService from '@/components/modal-terms-of-service';
import NewCampaignWizardModal from '@/components/new-campaign-wizard-modal';
import useAnalyticsLtxTeamIdentifier from '@/hooks/use-analytics-ltx-team-identifier';
import useCampaignCreationScreenChangedEvent from '@/hooks/use-campaign-creation-screen-changed-event/useCampaignCreationScreenChangeEvent';
import useDeltaEventScreenChanged from '@/hooks/use-delta-event-screen-changed';
import useFullStoryUser from '@/hooks/use-fullstory-user';
import useRevalidateSession from '@/hooks/use-revalidate-session';
import useSentryUser from '@/hooks/use-sentry-user';
import useSetLaunchDarklyContext from '@/hooks/use-set-launch-darkly-context';
import useSyncDeltaBaseAttributes from '@/hooks/use-sync-delta-base-attributes';
import useToken from '@/hooks/use-token';
import useZendeskScript from '@/hooks/use-zendesk-script';
import AppProviders from '@/AppProviders';
import ModestWebProvider, {
  useModestWebContext,
} from '@/contexts/ModestWebContext';
import '@/index.scss';
import './app.scss';
import ContentLabApp from './src/contentlab/App';

function App() {
  enableMapSet();
  useSentryUser();
  useFullStoryUser();
  const location = useLocation();

  useEffect(() => {
    const pagePath = location.pathname + location.search;
    AnalyticsService.dispatchPageView(pagePath);
    pageViewEvents();
  }, [location]);

  const isRevalidatingSession = useRevalidateSession({
    signupRoute: `${ROUTES.SIGN_UP}${location.search ? `${location.search}` : ''}`,
    loginRoute: `${ROUTES.LOGIN}${location.search ? `${location.search}` : ''}`,
  });
  const {flags, launchDarklyInitialized} = useSetLaunchDarklyContext();

  const modestContext = useModestWebContext();
  const modestInitialized = modestContext?.modestInitialized ?? false;

  const {showZendeskScript} = flags;
  useZendeskScript(getEnv().VITE_ZENDESK_KEY, showZendeskScript);

  if (isRevalidatingSession || !launchDarklyInitialized || !modestInitialized) {
    return <PoppaysLoader />;
  }

  return <MainRoutes flags={flags} />;
}

function BrandsApp() {
  const {isAuthenticated} = useToken(true, true);
  useSyncDeltaBaseAttributes(isAuthenticated);
  useAnalyticsLtxTeamIdentifier(isAuthenticated);
  useDeltaEventScreenChanged();
  useCampaignCreationScreenChangedEvent();

  return (
    <>
      <Routes>
        {PublicAccessRoutes()}
        <Route
          path={`${ROUTES.OFF_PLATFORM_REVIEW}/*`}
          element={<OffPlatformReviewRoutes />}
        />
        <Route path="/*" element={<App />} />
      </Routes>
      <ModalLeverLimit />
      <ModalContactSales />
      <ModalSubscriptionWrapper />
      <ModalTermsOfService />
      <NewCampaignWizardModal />
      <CreatorProfileModal />
      <ContentSubmissionAnalysisModal />
    </>
  );
}

function WrappedApp() {
  // all of various providers should be wrapping the <App /> component in here
  return (
    <AppProviders>
      <>
        <Router>
          <ModestWebProvider
            debug={getEnv().MODEST_WEB_DEBUG_MODE === 'true'}
            enabled={window.location.protocol === 'https:'}
          >
            <Routes>
              <Route
                path={`${CL_ROUTES.CONTENT_LAB}/*`}
                element={<ContentLabApp />}
              />
              <Route path="/*" element={<BrandsApp />} />
            </Routes>
            <DeveloperOverrideModal />
          </ModestWebProvider>
        </Router>
        <FlashMessagesService />
      </>
    </AppProviders>
  );
}

export default WrappedApp;
